import React, { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import './priceLog.css';

const LEVELS = ['ALL', 'PRODUCT', 'VARIANT', 'BOTH'];

const isEmpty = v => v === null || v === undefined || v === '';
const isBadNum = v => Number.isNaN(Number(v));
const fmtMoney = n => (isEmpty(n) || isBadNum(n) ? '-' : `₹${Number(n).toFixed(2)}`);
const fmtNum = n => (isEmpty(n) || isBadNum(n) ? '-' : `${Number(n)}`);

const fmtDateTime = d => {
  if (!d) return '-';
  const dt = new Date(d);
  return Number.isNaN(dt.getTime()) ? '-' : dt.toLocaleString();
};

function RowDiff({ label, oldV, newV, type = 'text' }) {
  const same = (oldV ?? null) === (newV ?? null) || (type === 'num' && Number(oldV ?? 0) === Number(newV ?? 0));

  const renderVal = v => {
    if (type === 'money') return fmtMoney(v);
    if (type === 'num') return fmtNum(v);
    return isEmpty(v) ? '-' : String(v);
  };

  return (
    <div className={`plog-row ${same ? 'same' : 'changed'}`}>
      <div className="plog-row-label">{label}</div>
      <div className="plog-row-old">{renderVal(oldV)}</div>
      <div className="plog-row-arrow">→</div>
      <div className="plog-row-new">{renderVal(newV)}</div>
    </div>
  );
}

function VariantBlock({ v }) {
  const title = v?.skuId ? `skuId : ${v.skuId}` : 'Variant';

  const attrText = Array.isArray(v?.skuatribute)
    ? v.skuatribute
        .map(a => {
          if (!a) return '';
          if (typeof a === 'string') return a;
          if (typeof a === 'object') return a.valueTrans || a.value || a.name || '';
          return String(a);
        })
        .filter(Boolean)
        .join(' • ')
    : '';

  const dimsOld = v?.old_dimensions || {};
  const dimsNew = v?.new_dimensions || {};

  const anyChanged =
    (v?.old_finalPrice ?? null) !== (v?.new_finalPrice ?? null) ||
    (v?.old_qty ?? null) !== (v?.new_qty ?? null) ||
    (v?.old_percentage ?? null) !== (v?.new_percentage ?? null) ||
    (v?.old_targetPrice ?? null) !== (v?.new_targetPrice ?? null) ||
    (v?.old_finalPriceMRP ?? null) !== (v?.new_finalPriceMRP ?? null) ||
    (v?.old_wholesaleFinalPrice ?? null) !== (v?.new_wholesaleFinalPrice ?? null) ||
    (v?.old_wholesalePercentage ?? null) !== (v?.new_wholesalePercentage ?? null) ||
    (v?.old_wholesaleTargetPrice ?? null) !== (v?.new_wholesaleTargetPrice ?? null) ||
    (v?.old_weight ?? null) !== (v?.new_weight ?? null) ||
    (dimsOld?.length ?? null) !== (dimsNew?.length ?? null) ||
    (dimsOld?.width ?? null) !== (dimsNew?.width ?? null) ||
    (dimsOld?.height ?? null) !== (dimsNew?.height ?? null);

  return (
    <div className="plog-variant">
      <div className="plog-variant-head">
        <div className="plog-variant-title">
          <strong>{title}</strong>
          {attrText ? <span className="plog-variant-sub">{attrText}</span> : null}
        </div>
      </div>

      <div className="plog-variant-grid">
        {/* Dropshipping fields */}
        {v?.pricingMode === 'dropshipping' && (v?.old_finalPrice ?? null) !== (v?.new_finalPrice ?? null) && <RowDiff label="Final Price" oldV={v?.old_finalPrice} newV={v?.new_finalPrice} type="money" />}
        {v?.pricingMode === 'dropshipping' && (v?.old_percentage ?? null) !== (v?.new_percentage ?? null) && <RowDiff label="Percentage" oldV={v?.old_percentage} newV={v?.new_percentage} type="num" />}
        {v?.pricingMode === 'dropshipping' && (v?.old_targetPrice ?? null) !== (v?.new_targetPrice ?? null) && <RowDiff label="Target Price" oldV={v?.old_targetPrice} newV={v?.new_targetPrice} type="money" />}
        {v?.pricingMode === 'dropshipping' && (v?.old_finalPriceMRP ?? null) !== (v?.new_finalPriceMRP ?? null) && <RowDiff label="MRP" oldV={v?.old_finalPriceMRP} newV={v?.new_finalPriceMRP} type="money" />}

        {/* Wholesale fields */}
        {v?.pricingMode === 'wholesale' && (v?.old_wholesaleFinalPrice ?? null) !== (v?.new_wholesaleFinalPrice ?? null) && <RowDiff label="Wholesale Final Price" oldV={v?.old_wholesaleFinalPrice} newV={v?.new_wholesaleFinalPrice} type="money" />}
        {v?.pricingMode === 'wholesale' && (v?.old_wholesalePercentage ?? null) !== (v?.new_wholesalePercentage ?? null) && <RowDiff label="Wholesale Percentage" oldV={v?.old_wholesalePercentage} newV={v?.new_wholesalePercentage} type="num" />}
        {v?.pricingMode === 'wholesale' && (v?.old_wholesaleTargetPrice ?? null) !== (v?.new_wholesaleTargetPrice ?? null) && <RowDiff label="Wholesale Target Price" oldV={v?.old_wholesaleTargetPrice} newV={v?.new_wholesaleTargetPrice} type="money" />}

        {/* Common fields */}
        {(v?.old_qty ?? null) !== (v?.new_qty ?? null) && <RowDiff label="Qty" oldV={v?.old_qty} newV={v?.new_qty} type="num" />}
        {(v?.old_weight ?? null) !== (v?.new_weight ?? null) && <RowDiff label="Weight" oldV={v?.old_weight} newV={v?.new_weight} type="num" />}
        {(dimsOld?.length ?? null) !== (dimsNew?.length ?? null) && <RowDiff label="Length" oldV={dimsOld?.length} newV={dimsNew?.length} type="num" />}
        {(dimsOld?.width ?? null) !== (dimsNew?.width ?? null) && <RowDiff label="Width" oldV={dimsOld?.width} newV={dimsNew?.width} type="num" />}
        {(dimsOld?.height ?? null) !== (dimsNew?.height ?? null) && <RowDiff label="Height" oldV={dimsOld?.height} newV={dimsNew?.height} type="num" />}

        {!anyChanged && <div className="plog-empty-sm">No changed fields in this variant.</div>}
      </div>

      {v?.remark && (
        <div className="plog-remark">
          <div className="plog-adminline">
            <strong>Admin:</strong> {v.adminEmail || v.adminName || v.userIdValue || v.userId || '-'}
          </div>
          <strong>Remark:</strong> {v.remark}
        </div>
      )}
    </div>
  );
}

function ConfirmModal({ open, title, description, loading, remark, setRemark, onCancel, onConfirm }) {
  useEffect(() => {
    if (!open) return;

    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden'; // stop background page scroll

    const onKey = e => {
      if (e.key === 'Escape') onCancel?.();
    };
    window.addEventListener('keydown', onKey);

    return () => {
      document.body.style.overflow = prevOverflow;
      window.removeEventListener('keydown', onKey);
    };
  }, [open, onCancel]);

  if (!open) return null;

  return createPortal(
    <div className="plog-modal-backdrop" onMouseDown={onCancel}>
      <div className="plog-modal" onMouseDown={e => e.stopPropagation()}>
        <div className="plog-modal-head">
          <div className="plog-modal-title">
            <span className="plog-modal-warning-icon">⚠️</span>
            {title}
          </div>
          <button type="button" className="plog-modal-x" onClick={onCancel} disabled={loading}>
            ×
          </button>
        </div>

        <div className="plog-modal-desc">{description}</div>

        <div className="plog-modal-body">
          <label className="plog-modal-label">
            Remark <span style={{ color: '#ef4444' }}>*</span>
          </label>

          <textarea className="plog-modal-textarea" value={remark} onChange={e => setRemark(e.target.value)} rows={3} placeholder="Write a short reason..." disabled={loading} />
          <div className="plog-modal-hint">
            This will reset <b>SP + all variants</b> to the <b>oldest values</b> from PriceLogs.
          </div>
        </div>

        <div className="plog-modal-foot">
          <button type="button" className="plog-modal-btn ghost" onClick={onCancel} disabled={loading}>
            Cancel
          </button>
          <button type="button" className="plog-modal-btn danger" onClick={onConfirm} disabled={loading || !remark.trim()}>
            {loading ? 'Setting...' : 'Yes, Set Actual'}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}

export default function PriceLogPanel({ offerId, productId, productData, logs = [], loading = false, onRefresh, onSaveActual, afterSave }) {
  const data = Array.isArray(logs) ? logs : [];

  const [levelFilter, setLevelFilter] = useState('ALL');
  const [query, setQuery] = useState('');
  const [openId, setOpenId] = useState(null);
  const [pricingModeFilter, setPricingModeFilter] = useState('all'); // 'all', 'dropshipping', 'wholesale'

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmRemark, setConfirmRemark] = useState('Set actual values from oldest PriceLog');
  const [actionLoading, setActionLoading] = useState(false);

  useEffect(() => {
    setOpenId(data?.[0]?._id || null);
  }, [data]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();

    return [...data]
      .filter(l => (levelFilter === 'ALL' ? true : l.level === levelFilter))
      .filter(l => {
        // Filter by pricing mode
        if (pricingModeFilter !== 'all') {
          const logPricingMode = l.pricingMode || (l.variants && l.variants.length > 0 ? l.variants[0].pricingMode : null) || 'dropshipping';
          if (logPricingMode !== pricingModeFilter) return false;
        }
        return true;
      })
      .filter(l => {
        if (!q) return true;
        const hay = [l._id, l.level, l.status, l.offerId, l.remark, ...(l.variants || []).map(v => `${v.skuId || ''} ${v.specId || ''} ${v.remark || ''}`)]
          .filter(Boolean)
          .join(' ')
          .toLowerCase();
        return hay.includes(q);
      })
      .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
  }, [data, levelFilter, query, pricingModeFilter]);

  const runSetActualAll = async () => {
    try {
      if (!productId) return alert('ProductId missing');
      if (!productData) return alert('Product data missing');
      if (!data.length) return alert('No price logs found');
      if (typeof onSaveActual !== 'function') return alert('Save handler missing');

      setActionLoading(true);

      const sortedOldestFirst = [...data].sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));

      // oldest SP
      let originalSp = null;
      for (const l of sortedOldestFirst) {
        if (l?.old_sp !== null && typeof l?.old_sp !== 'undefined') {
          originalSp = l.old_sp;
          break;
        }
      }

      const keyOf = (skuId, specId) => `${skuId ?? ''}::${specId ?? ''}`;
      const originalVariant = new Map();

      for (const l of sortedOldestFirst) {
        for (const v of l?.variants || []) {
          const k = keyOf(v?.skuId, v?.specId);
          if (!originalVariant.has(k)) originalVariant.set(k, { dimensions: {} });
          const o = originalVariant.get(k);

          // Dropshipping pricing fields
          if (o.finalPrice == null && v?.old_finalPrice != null) o.finalPrice = v.old_finalPrice;
          if (o.percentage === undefined && Object.prototype.hasOwnProperty.call(v, 'old_percentage')) {
            o.percentage = v.old_percentage;
          }
          if (o.targetPrice === undefined && Object.prototype.hasOwnProperty.call(v, 'old_targetPrice')) {
            o.targetPrice = v.old_targetPrice;
          }

          // Wholesale pricing fields
          if (o.wholesaleFinalPrice == null && v?.old_wholesaleFinalPrice != null) o.wholesaleFinalPrice = v.old_wholesaleFinalPrice;
          if (o.wholesalePercentage === undefined && Object.prototype.hasOwnProperty.call(v, 'old_wholesalePercentage')) {
            o.wholesalePercentage = v.old_wholesalePercentage;
          }
          if (o.wholesaleTargetPrice === undefined && Object.prototype.hasOwnProperty.call(v, 'old_wholesaleTargetPrice')) {
            o.wholesaleTargetPrice = v.old_wholesaleTargetPrice;
          }

          // Common fields
          if (o.qty == null && v?.old_qty != null) o.qty = v.old_qty;
          if (o.weight == null && v?.old_weight != null) o.weight = v.old_weight;

          if (o.dimensions.length == null && v?.old_dimensions?.length != null) o.dimensions.length = v.old_dimensions.length;
          if (o.dimensions.width == null && v?.old_dimensions?.width != null) o.dimensions.width = v.old_dimensions.width;
          if (o.dimensions.height == null && v?.old_dimensions?.height != null) o.dimensions.height = v.old_dimensions.height;
        }
      }

      const currentVars = Array.isArray(productData?.variations) ? productData.variations : [];
      const updatedVariations = currentVars.map(vv => {
        const k = keyOf(vv?.skuId, vv?.specId);
        const orig = originalVariant.get(k);
        if (!orig) return vv;

        const next = { ...vv };

        // Restore based on pricing mode filter
        // If 'all' is selected, restore both dropshipping and wholesale
        // If 'dropshipping' is selected, only restore dropshipping fields
        // If 'wholesale' is selected, only restore wholesale fields
        if (pricingModeFilter === 'all' || pricingModeFilter === 'dropshipping') {
          // Dropshipping pricing fields
          if (orig.finalPrice != null) next.finalPrice = orig.finalPrice;
          if (Object.prototype.hasOwnProperty.call(orig, 'percentage')) next.percentage = orig.percentage;
          if (Object.prototype.hasOwnProperty.call(orig, 'targetPrice')) next.targetPrice = orig.targetPrice;
          if (next.finalPrice != null) {
            next.finalPriceMRP = Number.isFinite(Number(next.finalPrice)) ? Number(next.finalPrice) * 2.5 : null;
          }
        }

        if (pricingModeFilter === 'all' || pricingModeFilter === 'wholesale') {
          // Wholesale pricing fields
          if (orig.wholesaleFinalPrice != null) next.wholesaleFinalPrice = orig.wholesaleFinalPrice;
          if (Object.prototype.hasOwnProperty.call(orig, 'wholesalePercentage')) next.wholesalePercentage = orig.wholesalePercentage;
          if (Object.prototype.hasOwnProperty.call(orig, 'wholesaleTargetPrice')) next.wholesaleTargetPrice = orig.wholesaleTargetPrice;
        }

        // Common fields (always restore)
        if (orig.qty != null) {
          if (Object.prototype.hasOwnProperty.call(next, 'amountOnSale')) next.amountOnSale = orig.qty;
          else next.qty = orig.qty;
        }

        if (orig.weight != null) next.weight = orig.weight;

        if (orig.dimensions?.length != null) next.length = orig.dimensions.length;
        if (orig.dimensions?.width != null) next.width = orig.dimensions.width;
        if (orig.dimensions?.height != null) next.height = orig.dimensions.height;

        return next;
      });

      const payload = {
        ...productData,
        sp: originalSp != null ? originalSp : productData?.sp,
        mrp: (originalSp != null ? Number(originalSp) : Number(productData?.sp)) * 2.5,
        variations: updatedVariations.map(variation => ({
          ...variation,
          skuId: variation.skuId,
          specId: variation.specId,
          amountOnSale: variation.amountOnSale,
          consignPrice: variation.consignPrice,
          weight: variation.weight,
          length: variation.length,
          height: variation.height,
          width: variation.width,
          actualPrice: variation.actualPrice,
          finalPrice: variation.finalPrice,
          finalPriceMRP: Number.isFinite(Number(variation.finalPrice)) ? Number(variation.finalPrice) * 2.5 : null,
          price: variation.price,
          fenxiaoPriceInfo: variation.fenxiaoPriceInfo,
          shippingDetail: variation.shippingDetail,
          percentage: variation.percentage,
          targetPrice: variation.targetPrice,
          // Wholesale pricing fields
          wholesaleFinalPrice: variation.wholesaleFinalPrice,
          wholesalePercentage: variation.wholesalePercentage,
          wholesaleTargetPrice: variation.wholesaleTargetPrice,
        })),

        remark: (confirmRemark || '').trim() || 'Set actual values from oldest PriceLog',
      };

      await onSaveActual(payload);

      if (typeof afterSave === 'function') await afterSave();
      else if (typeof onRefresh === 'function') await onRefresh();
    } catch (e) {
      console.error(e);
      alert('Set Actual Price failed');
    } finally {
      setActionLoading(false);
    }
  };

  return (
    <aside className="plog-panel">
      <ConfirmModal
        open={confirmOpen}
        title="Confirm: Set Actual Price ?"
        description="This action will reset SP + all variants to the oldest logged values."
        loading={actionLoading}
        remark={confirmRemark}
        setRemark={setConfirmRemark}
        onCancel={() => (actionLoading ? null : setConfirmOpen(false))}
        onConfirm={async () => {
          if (actionLoading) return;
          setConfirmOpen(false);
          await runSetActualAll();
        }}
      />

      <div className="plog-head">
        <div>
          <h3 className="plog-title">Price Log History</h3>
          <div className="plog-sub">
            <span>Offer Id :</span> <b>{offerId || '-'}</b>
          </div>
        </div>

        <div className="plog-actions">
          <button
            type="button"
            className="plog-btn"
            onClick={() => {
              setConfirmRemark('Set actual values from oldest PriceLog');
              setConfirmOpen(true);
            }}
            disabled={loading || actionLoading || pricingModeFilter === 'all'}
            title={
              pricingModeFilter === 'all' 
                ? "Please select Dropshipping or Wholesale tab to set actual price"
                : pricingModeFilter === 'wholesale'
                ? "Reset SP + wholesale variant fields to oldest values"
                : "Reset SP + dropshipping variant fields to oldest values"
            }
          >
            {pricingModeFilter === 'all' 
              ? 'Set Actual Price'
              : pricingModeFilter === 'wholesale'
              ? 'WS Set Actual Price'
              : 'DS Set Actual Price'}
          </button>

          <button
            type="button"
            className="plog-refresh"
            onClick={() => (typeof onRefresh === 'function' ? onRefresh() : null)}
            title="Refresh"
            disabled={loading || actionLoading}
            style={loading ? { opacity: 0.6, cursor: 'not-allowed' } : undefined}
          >
            ↻
          </button>
        </div>
      </div>
      
      {/* Pricing Mode Tabs - Separate Row Below Set Actual Price */}
      <div style={{ display: "flex", gap: 8, marginTop: 12, marginBottom: 8, borderBottom: "1px solid #e5e7eb", paddingBottom: 8 }}>
        <button
          type="button"
          onClick={() => setPricingModeFilter('all')}
          style={{
            padding: "6px 12px",
            height: "32px",
            border: "none",
            background: pricingModeFilter === 'all' ? "#2563eb" : "transparent",
            color: pricingModeFilter === 'all' ? "#fff" : "#6b7280",
            cursor: "pointer",
            borderBottom: pricingModeFilter === 'all' ? "2px solid #2563eb" : "2px solid transparent",
            fontWeight: pricingModeFilter === 'all' ? 600 : 400,
            fontSize: 12,
            display: "flex",
            alignItems: "center",
          }}
        >
          All
        </button>
        <button
          type="button"
          onClick={() => setPricingModeFilter('dropshipping')}
          style={{
            padding: "6px 12px",
            height: "32px",
            border: "none",
            background: pricingModeFilter === 'dropshipping' ? "#2563eb" : "transparent",
            color: pricingModeFilter === 'dropshipping' ? "#fff" : "#6b7280",
            cursor: "pointer",
            borderBottom: pricingModeFilter === 'dropshipping' ? "2px solid #2563eb" : "2px solid transparent",
            fontWeight: pricingModeFilter === 'dropshipping' ? 600 : 400,
            fontSize: 12,
            display: "flex",
            alignItems: "center",
          }}
        >
          Dropshipping
        </button>
        <button
          type="button"
          onClick={() => setPricingModeFilter('wholesale')}
          style={{
            padding: "6px 12px",
            height: "32px",
            border: "none",
            background: pricingModeFilter === 'wholesale' ? "#2563eb" : "transparent",
            color: pricingModeFilter === 'wholesale' ? "#fff" : "#6b7280",
            cursor: "pointer",
            borderBottom: pricingModeFilter === 'wholesale' ? "2px solid #2563eb" : "2px solid transparent",
            fontWeight: pricingModeFilter === 'wholesale' ? 600 : 400,
            fontSize: 12,
            display: "flex",
            alignItems: "center",
          }}
        >
          Wholesale
        </button>
      </div>

      <div className="plog-controls">
        <div className="plog-tabs">
          {LEVELS.map(t => (
            <button key={t} type="button" className={`plog-tab ${levelFilter === t ? 'active' : ''}`} onClick={() => setLevelFilter(t)}>
              {t}
            </button>
          ))}
        </div>

        <input className="plog-search" placeholder="Search skuId/specId/remark/field..." value={query} onChange={e => setQuery(e.target.value)} />
      </div>

      <div className="plog-list">
        {loading ? (
          <div className="plog-empty">Loading logs...</div>
        ) : filtered.length === 0 ? (
          <div className="plog-empty">No logs found.</div>
        ) : (
          filtered.map(log => {
            const isOpen = openId === log._id;
            const hasProductChange = log.old_sp !== null || log.new_sp !== null;
            const variantCount = Array.isArray(log.variants) ? log.variants.length : 0;

            return (
              <div key={log._id} className={`plog-card ${isOpen ? 'open' : ''}`}>
                <button type="button" className="plog-card-head" onClick={() => setOpenId(prev => (prev === log._id ? null : log._id))}>
                  <div className="plog-card-left">
                    <div className="plog-meta">
                      <span className={`plog-badge lvl-${String(log.level || '').toLowerCase()}`}>{log.level}</span>
                      {variantCount ? <span className="plog-badge gray">{variantCount} variant</span> : null}
                    </div>
                    <div className="plog-date">{fmtDateTime(log.createdAt)}</div>
                  </div>

                  <div className="plog-card-right">
                    {hasProductChange && <div className="plog-sp" />}
                    <div className="plog-caret">{isOpen ? '▾' : '▸'}</div>
                  </div>
                </button>

                {isOpen ? (
                  <div className="plog-card-body">
                    {hasProductChange && (log.old_sp ?? null) !== (log.new_sp ?? null) ? (
                      <div className="plog-section">
                        <div className="plog-section-title">Product Price</div>
                        <RowDiff label="Selling Price (SP)" oldV={log.old_sp} newV={log.new_sp} type="money" />
                        {log.remark ? (
                          <div className="plog-remark">
                            <div className="plog-adminline">
                              <strong>Admin:</strong> {log.adminEmail || log.adminName || log.userIdValue || log.userId || '-'}
                            </div>
                            <strong>Remark:</strong> {log.remark}
                          </div>
                        ) : null}
                      </div>
                    ) : null}

                    <div className="plog-section">
                      <div className="plog-section-title">Variant Changes</div>
                      {variantCount === 0 ? (
                        <div className="plog-empty-sm">No variant changes in this log.</div>
                      ) : (
                        <div className="plog-variants">
                          {log.variants.map((v, idx) => (
                            <VariantBlock key={`${log._id}-${v?.skuId || idx}`} v={v} />
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                ) : null}
              </div>
            );
          })
        )}
      </div>
    </aside>
  );
}
